import { ChangeEvent, FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Typography, Box, CircularProgress } from '@mui/material';
import { Input } from '@/UI/Input/Input';
import { color } from '@/colors';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { FlexCenVer } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';

export interface SaveAssetModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, description: string) => void;
  title: string;
  saveButtonLabel: string;
  initialName?: string;
  initialDescription?: string;
  isSaving?: boolean;
}

export function SaveAssetModal({
  open,
  onClose,
  onSave,
  title,
  saveButtonLabel,
  initialName = '',
  initialDescription = '',
  isSaving = false,
}: SaveAssetModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(name.trim(), description.trim());
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: color.Black84_T,
        },
        '& .MuiDialog-paper': {
          backgroundImage: 'none',
          borderRadius: '8px',
          border: `1px solid ${color.White08_T}`,
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: { xs: '90%', sm: '420px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          p: 3,
          pt: 5.25,
          pb: 3,
          backgroundColor: color.Black92,
        }}
      >
        <Typography variant="body-lg-sb">{title}</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Input
            id="save-asset-name"
            label="Name*"
            value={name}
            onChange={handleNameChange}
            required
            autoFocus
            noAutoSelect
            noBlurOnEnter
            autoComplete="off"
          />
          <Input
            id="save-asset-description"
            label="Description"
            value={description}
            onChange={handleDescriptionChange}
            multiline
            rows={5}
            noBlurOnEnter
            noAutoSelect
            sx={{
              '& .MuiInputBase-root': {
                alignItems: 'flex-start',
                height: '100px',
              },
              '& .MuiInputBase-input': {
                height: 'auto !important',
                overflow: 'auto !important',
              },
            }}
          />
        </Box>

        <FlexCenVer sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <ButtonContained size="small" mode="text" onClick={onClose} sx={{ px: 1 }}>
            {t(I18N_KEYS.GENERAL.CANCEL)}
          </ButtonContained>
          <ButtonContained
            size="small"
            type="submit"
            mode="filled-light"
            disabled={isSaving || !name.trim()}
            endIcon={isSaving ? <CircularProgress size={16} sx={{ color: color.White64_T }} /> : null}
          >
            {saveButtonLabel}
          </ButtonContained>
        </FlexCenVer>
      </Box>
      <AppXBtn sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose} />
    </Dialog>
  );
}
